import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  IconButton,
  Avatar,
  Tooltip,
  Fade
} from "@material-ui/core";
import MicIcon from "@material-ui/icons/Mic";
import MicOffIcon from "@material-ui/icons/MicOff";
import VideocamIcon from "@material-ui/icons/Videocam";
import VideocamOffIcon from "@material-ui/icons/VideocamOff";
import PersonIcon from "@material-ui/icons/Person";
import { RecordingConsentScreen } from "./RecordingConsentScreen";
import { color, fontFamily, fontSize } from "../../style/constants";

enum Steps {
  recordingConsent,
  deviceSelection
}

interface PreJoinScreensProps {
  onJoin: (
    accepted: boolean,
    recordingAllowed?: boolean,
    mediaSettings?: { video: boolean; audio: boolean },
    virtualBackground?: { imageUrl?: string }
  ) => void;
  username: string;
  showRecordingConsent?: boolean;
}

export const PreJoinScreens: React.FC<PreJoinScreensProps> = ({
  onJoin,
  username,
  showRecordingConsent = true
}) => {
  const [step, setStep] = useState(
    showRecordingConsent ? Steps.recordingConsent : Steps.deviceSelection
  );
  const [recordingAllowed, setRecordingAllowed] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (step === Steps.deviceSelection) {
      const startPreview = async () => {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: videoEnabled,
            audio: audioEnabled
          });
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
        } catch (error) {
          console.error("Failed to get media stream:", error);
        }
      };

      startPreview();

      return () => {
        if (videoRef.current?.srcObject) {
          const stream = videoRef.current.srcObject as MediaStream;
          stream.getTracks().forEach(track => track.stop());
        }
      };
    }
  }, [step, audioEnabled, videoEnabled]);

  const handleRecordingConsent = (allowed: boolean) => {
    setRecordingAllowed(allowed);
    setStep(Steps.deviceSelection);
  };

  if (step === Steps.recordingConsent) {
    return <RecordingConsentScreen onConsent={handleRecordingConsent} />;
  }

  return (
    <Box className="zoom-app-wrapper">
      <Card elevation={3} className="zoom-app-card">
        <CardContent className="zoom-app-content">
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            p={4}
            width="100%"
            style={{ minHeight: 0 }}
          >
            <Typography
              variant="h4"
              style={{
                fontFamily: fontFamily.primary,
                fontWeight: 700,
                fontSize: fontSize.large,
                color: color.NETURALS900,
                marginBottom: 8,
                letterSpacing: "-0.02em"
              }}
            >
              Ready to join?
            </Typography>

            <Typography
              variant="body1"
              style={{
                fontFamily: fontFamily.primary,
                fontSize: fontSize.normal,
                color: color.LIGHTGRAY,
                marginBottom: 32,
                textAlign: "center"
              }}
            >
              Check your camera and microphone settings
            </Typography>

            <Box
              position="relative"
              width="100%"
              maxWidth={520}
              height={360}
              borderRadius={16}
              overflow="hidden"
              boxShadow="0 8px 32px rgba(0,0,0,0.12)"
              mb={3}
              style={{
                background: !videoEnabled
                  ? `linear-gradient(135deg, ${color.DARKGRAY} 0%, ${color.GRAPHITE} 100%)`
                  : undefined,
                backgroundImage: process.env.REACT_APP_ZOOM_BG_IMAGE
                  ? `url(${process.env.REACT_APP_ZOOM_BG_IMAGE})`
                  : undefined,
                backgroundSize: "cover",
                backgroundPosition: "center",
                minHeight: 200
              }}
            >
              {videoEnabled ? (
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: 16,
                    opacity: process.env.REACT_APP_ZOOM_BG_IMAGE ? 0.8 : 1
                  }}
                />
              ) : (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                >
                  <Avatar
                    style={{
                      width: 80,
                      height: 80,
                      backgroundColor: color.BLUE,
                      marginBottom: 20,
                      boxShadow: "0 4px 16px rgba(3, 155, 229, 0.3)"
                    }}
                  >
                    <PersonIcon style={{ fontSize: 48 }} />
                  </Avatar>
                  <Typography
                    style={{
                      color: color.WHITE,
                      fontFamily: fontFamily.primary,
                      fontWeight: 600,
                      fontSize: fontSize.medium
                    }}
                  >
                    Camera is off
                  </Typography>
                </Box>
              )}

              <Box
                position="absolute"
                bottom={0}
                left={0}
                right={0}
                p={2}
                style={{
                  background: "linear-gradient(transparent, rgba(0,0,0,0.7))"
                }}
                display="flex"
                alignItems="center"
              >
                <Typography
                  style={{
                    color: color.WHITE,
                    fontFamily: fontFamily.primary,
                    fontWeight: 600,
                    fontSize: fontSize.normal,
                    marginLeft: 8
                  }}
                >
                  {username}
                </Typography>
              </Box>
            </Box>

            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              mb={3}
              style={{ gap: 16, flexWrap: "wrap" }}
            >
              <Tooltip
                title={audioEnabled ? "Mute microphone" : "Unmute microphone"}
                TransitionComponent={Fade}
                arrow
              >
                <IconButton
                  onClick={() => setAudioEnabled(!audioEnabled)}
                  className={`zoom-control-btn ${!audioEnabled ? "muted" : ""}`}
                  style={{
                    width: 56,
                    height: 56,
                    backgroundColor: audioEnabled ? color.WHITE : color.DARKRED,
                    color: audioEnabled ? color.DARKGRAY : color.WHITE,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                    transition: "all 0.3s ease"
                  }}
                >
                  {audioEnabled ? <MicIcon /> : <MicOffIcon />}
                </IconButton>
              </Tooltip>

              <Tooltip
                title={videoEnabled ? "Turn off camera" : "Turn on camera"}
                TransitionComponent={Fade}
                arrow
              >
                <IconButton
                  onClick={() => setVideoEnabled(!videoEnabled)}
                  className={`zoom-control-btn ${
                    !videoEnabled ? "disabled" : ""
                  }`}
                  style={{
                    width: 56,
                    height: 56,
                    backgroundColor: videoEnabled
                      ? color.WHITE
                      : color.DARKGRAY,
                    color: videoEnabled ? color.DARKGRAY : color.WHITE,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                    transition: "all 0.3s ease"
                  }}
                >
                  {videoEnabled ? <VideocamIcon /> : <VideocamOffIcon />}
                </IconButton>
              </Tooltip>

              <Button
                variant="contained"
                color="primary"
                size="large"
                className="zoom-join-btn"
                onClick={() =>
                  onJoin(
                    true,
                    recordingAllowed,
                    {
                      video: videoEnabled,
                      audio: audioEnabled
                    },
                    {
                      imageUrl: process.env.REACT_APP_ZOOM_BG_IMAGE
                    }
                  )
                }
                style={{
                  minWidth: 200,
                  height: 56,
                  borderRadius: 28,
                  fontFamily: fontFamily.primary,
                  fontWeight: 700,
                  fontSize: fontSize.medium,
                  textTransform: "none",
                  background: `linear-gradient(135deg, ${color.BLUE} 0%, ${color.DARKBLUE} 100%)`,
                  boxShadow: "0 8px 24px rgba(3, 125, 186, 0.4)",
                  transition: "all 0.3s ease",
                  marginLeft: 16
                }}
              >
                Join Meeting
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
